import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import JournalNavigation from '../../components/JournalNavigation';
import LockIndicator from '../../components/LockIndicator';
import JournalStartModal from '../../components/JournalStartModal';
import { getJournalStartConfig } from '../../utils/journalConstants';
import '../journal/SelfAssessment.css';

const CurrentHealth = () => {
    const [showStartModal, setShowStartModal] = useState(false);
    const [hasStarted, setHasStarted] = useState(false);
    const [formData, setFormData] = useState({
        age: '',
        bloodPressure: '',
        weight: '',
        height: '',
        fastingGlucose: '',
        waistCircumference: '',
        bodyAppreciation: '',
        symptomsAndImprovements: '',
        symptom1: '', rating1: '',
        symptom2: '', rating2: '',
        symptom3: '', rating3: '',
        symptom4: '', rating4: '',
        symptom5: '', rating5: '',
        symptom6: '', rating6: '',
        symptom7: '', rating7: '',
        symptom8: '', rating8: '',
        symptom9: '', rating9: '',
        symptom10: '', rating10: '',
        selectedSymptoms: {}
    });
    const [saveStatus, setSaveStatus] = useState('');
    const [entryId, setEntryId] = useState(null);
    const [entryMetadata, setEntryMetadata] = useState({ lockedAt: null, startedAt: null, createdAt: null });

    const symptomsList = [
        'insomnia', 'heartburn', 'joint pain', 'constipation',
        'sinus pressure', 'diarrhoea', 'swelling', 'excessive thirst',
        'palpitations', 'excessive hunger', 'abdominal pain', 'anxiety',
        'abdominal bloating', 'depression', 'abdominal gas', 'period pain/erectile dysfunction'
    ];

    useEffect(() => {
        loadEntry();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Reload when cycle changes
    useEffect(() => {
        const handler = () => { loadEntry(); };
        window.addEventListener('cycle-changed', handler);
        return () => window.removeEventListener('cycle-changed', handler);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadEntry = async () => {
        try {
            const response = await api.get('/journal', { params: { section: 'current-health' } });
            if (response.data.entries && response.data.entries.length > 0) {
                const entry = response.data.entries[0];
                setEntryId(entry._id);
                setFormData(prev => ({ ...prev, ...(entry.data || {}) }));
                setEntryMetadata({
                    lockedAt: entry.lockedAt,
                    startedAt: entry.startedAt,
                    createdAt: entry.createdAt
                });
                setHasStarted(!!entry.startedAt);
            }
        } catch (err) {
            console.error('Failed to load current health entry', err);
        }
    };

    // autosave debounce
    useEffect(() => {
        const t = setTimeout(() => {
            if (Object.values(formData).some(v => v && v !== '' && !(typeof v === 'object'))) {
                saveEntry();
            }
        }, 1200);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData]);

    const saveEntry = async (includeStartTime = false) => {
        try {
            setSaveStatus('saving');
            const payload = {
                section: 'current-health',
                data: formData
            };

            if (includeStartTime) {
                payload.startedAt = new Date().toISOString();
            }

            if (!entryId) {
                const res = await api.post('/journal', payload);
                setEntryId(res.data.entry._id);
                setEntryMetadata({
                    lockedAt: res.data.entry.lockedAt,
                    startedAt: res.data.entry.startedAt,
                    createdAt: res.data.entry.createdAt
                });
            } else {
                const response = await api.patch(`/journal/${entryId}`, payload);
                if (response.data.entry && response.data.entry.startedAt) {
                    setEntryMetadata(prev => ({
                        ...prev,
                        startedAt: response.data.entry.startedAt
                    }));
                }
            }
            setSaveStatus('saved');
            setTimeout(() => setSaveStatus(''), 1800);
        } catch (err) {
            console.error('Save failed', err);
            setSaveStatus('error');
        }
    };

    const handleChange = (key, value) => {
        if (!hasStarted) {
            if (!showStartModal) {
                setShowStartModal(true);
            }
            return; // Don't update form until user clicks "Begin"
        }
        setFormData(prev => ({ ...prev, [key]: value }));
    };

    const handleBegin = () => {
        setShowStartModal(false);
        setHasStarted(true);
        saveEntry(true);
    };

    const handleNotNow = () => {
        setShowStartModal(false);
        // Stay on the same page - user can try again later
    };

    const handleSymptomClick = (symptom) => {
        if (!hasStarted) {
            if (!showStartModal) {
                setShowStartModal(true);
            }
            return; // Don't update form until user clicks "Begin"
        }
        setFormData(prev => ({
            ...prev,
            selectedSymptoms: {
                ...prev.selectedSymptoms,
                [symptom]: !prev.selectedSymptoms[symptom]
            }
        }));
    };

    const isLocked = entryMetadata.lockedAt !== null;
    const modalConfig = getJournalStartConfig('current-health');


    return (
        <>
            <JournalStartModal
                isOpen={showStartModal}
                onBegin={handleBegin}
                onCancel={handleNotNow}
                title={modalConfig.title}
                message={modalConfig.message}
                estimatedTime={modalConfig.estimatedTime}
            />
            <div className="journal-page-wrapper">
                <div className={`save-status ${saveStatus}`}>
                    {saveStatus === 'saving' && '💾 Saving...'}
                    {saveStatus === 'saved' && '✓ Saved successfully'}
                    {saveStatus === 'error' && '⚠ Save failed'}
                </div>

                <LockIndicator
                    lockedAt={entryMetadata.lockedAt}
                    startedAt={entryMetadata.startedAt}
                />

                <div style={{ padding: '28px' }}>
                    <div style={{ marginBottom: '24px' }}>
                        <h1 className="journal-title">Current Health</h1>
                        <p className="journal-prompt">Reminder: we love ourselves right now — enough to keep improving.</p>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px', marginTop: '10px' }}>
                        <div>
                            <div className="journal-line" style={{ marginBottom: '20px' }}>
                                <label>Age</label>
                                <input type="text" value={formData.age} onChange={(e) => handleChange('age', e.target.value)} />
                            </div>

                            <div className="journal-line" style={{ marginBottom: '20px' }}>
                                <label>Weight</label>
                                <input type="text" value={formData.weight} onChange={(e) => handleChange('weight', e.target.value)} />
                            </div>

                            <div className="journal-line" style={{ marginBottom: '20px' }}>
                                <label>Fasting blood glucose</label>
                                <input type="text" value={formData.fastingGlucose} onChange={(e) => handleChange('fastingGlucose', e.target.value)} />
                            </div>
                        </div>

                        <div>
                            <div className="journal-line" style={{ marginBottom: '20px' }}>
                                <label>Blood Pressure</label>
                                <input type="text" value={formData.bloodPressure} onChange={(e) => handleChange('bloodPressure', e.target.value)} />
                            </div>

                            <div className="journal-line" style={{ marginBottom: '20px' }}>
                                <label>Height</label>
                                <input type="text" value={formData.height} onChange={(e) => handleChange('height', e.target.value)} />
                            </div>

                            <div className="journal-line" style={{ marginBottom: '20px' }}>
                                <label>Waist circumference</label>
                                <input type="text" value={formData.waistCircumference} onChange={(e) => handleChange('waistCircumference', e.target.value)} />
                            </div>
                        </div>
                    </div>

                    <div style={{ marginTop: '26px' }}>
                        <p className="journal-prompt">List everything that you appreciate about your body and mind.</p>
                        <div className="journal-line multi-line" style={{ marginTop: '12px' }}>
                            <textarea value={formData.bodyAppreciation} onChange={(e) => handleChange('bodyAppreciation', e.target.value)} rows={8} />
                        </div>
                    </div>

                    <div style={{ marginTop: '36px' }}>
                        <h2 className="journal-title" style={{ fontSize: '20px' }}>Current health status</h2>
                        <p className="journal-prompt">Write down all the symptoms that you are experiencing. List aspects you want to improve.</p>
                        <div className="journal-line multi-line" style={{ marginTop: '12px' }}>
                            <textarea value={formData.symptomsAndImprovements} onChange={(e) => handleChange('symptomsAndImprovements', e.target.value)} rows={10} />
                        </div>
                    </div>

                    <div style={{ marginTop: '36px' }}>
                        <p className="journal-prompt">List the symptoms/keywords that you have highlighted and give them a rating out of 10.</p>
                        <p style={{ fontSize: '14px', color: '#3d3425', marginTop: '12px' }}>
                            <strong>1</strong> = not severe — <strong>10</strong> = very severe
                        </p>

                        <div className="symptom-rating-container" style={{ marginTop: '20px' }}>
                            {[1, 3, 5, 7, 9].map((num) => (
                                <div key={num} className="symptom-rating-row">
                                    <div className="symptom-rating-item">
                                        <strong className="symptom-number">{num}.</strong>
                                        <div className="journal-line" style={{ flex: 1 }}>
                                            <input type="text" value={formData[`symptom${num}`]} onChange={(e) => handleChange(`symptom${num}`, e.target.value)} />
                                        </div>
                                        <div className="rating-input-wrapper">
                                            <input type="text" value={formData[`rating${num}`]} onChange={(e) => handleChange(`rating${num}`, e.target.value)} className="rating-input" />
                                            <span className="rating-divider">/10</span>
                                        </div>
                                    </div>

                                    <div className="symptom-rating-item">
                                        <strong className="symptom-number">{num + 1}.</strong>
                                        <div className="journal-line" style={{ flex: 1 }}>
                                            <input type="text" value={formData[`symptom${num + 1}`]} onChange={(e) => handleChange(`symptom${num + 1}`, e.target.value)} />
                                        </div>
                                        <div className="rating-input-wrapper">
                                            <input type="text" value={formData[`rating${num + 1}`]} onChange={(e) => handleChange(`rating${num + 1}`, e.target.value)} className="rating-input" />
                                            <span className="rating-divider">/10</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <p className="journal-prompt" style={{ marginTop: '20px' }}>Click any additional symptoms below that you have been experiencing and give it a rating out of ten.</p>

                        <div className="symptom-table-container" style={{ marginTop: '16px' }}>
                            <div className="symptom-table-grid">
                                <div className="symptom-table-header"><strong>Symptom</strong></div>
                                <div className="symptom-table-header symptom-table-rating-header"><strong>Rating out of 10</strong></div>
                                <div className="symptom-table-header symptom-table-header-right"><strong>Symptom</strong></div>
                                <div className="symptom-table-header symptom-table-rating-header symptom-table-header-last"><strong>Rating out of 10</strong></div>

                                {symptomsList.map((symptom, index) => (
                                    <React.Fragment key={symptom}>
                                        <div className={`symptom-table-cell ${index % 2 === 0 ? 'symptom-cell-dark' : 'symptom-cell-light'}`} style={{ cursor: 'pointer' }} onClick={() => handleSymptomClick(symptom)}>
                                            {symptom}
                                        </div>
                                        <div className={`symptom-table-cell symptom-rating-cell ${index % 2 === 0 ? 'rating-cell-light' : 'rating-cell-lighter'}`}>
                                            <input type="text" value={formData[`${symptom}_rating`] || ''} onChange={(e) => handleChange(`${symptom}_rating`, e.target.value)} className="symptom-rating-table-input" />
                                        </div>
                                    </React.Fragment>
                                ))}
                            </div>
                        </div>

                        <p style={{ fontSize: '14px', color: '#3d3425', marginTop: '16px', lineHeight: '1.6', fontStyle: 'italic' }}>
                            <strong>Reminder:</strong> if you haven't received a medical explanation for your symptoms, especially if they are severe, it's recommended you speak to a qualified health professional.
                        </p>

                        <JournalNavigation
                            prevPage="/journal/self-assessment"
                            nextPage="/journal/reflection"
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

export default CurrentHealth;
